import React, { useState, useCallback, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import AsyncSelect from 'react-select/async';
import Select from 'react-select';
import ApiClient from '../api/ApiClient';
import { useAuth } from '../contexts/AuthContext';

const PAGE_SIZE = 20;

// ── Results modal ──────────────────────────────────────────────────────────
function ResultModal({ result, onClose }) {
  if (!result) return null;

  const history = result.PortfolioHistory || [];
  const finalValue = history.length ? history[history.length - 1].TotalValue : null;
  const initialValue = history.length ? history[0].TotalValue : null;
  const gain = finalValue != null && initialValue != null ? finalValue - initialValue : null;
  const gainPct = gain != null && initialValue > 0 ? (gain / initialValue) * 100 : null;

  return (
    <div className="modal d-block" tabIndex="-1" style={{ background: 'rgba(0,0,0,0.5)' }}>
      <div className="modal-dialog modal-xl modal-dialog-scrollable">
        <div className="modal-content">
          <div className="modal-header">
            <h5 className="modal-title">Calculation Result</h5>
            <button type="button" className="btn-close" onClick={onClose} />
          </div>
          <div className="modal-body">
            {finalValue != null && (
              <div className="row g-2 mb-3">
                <div className="col-4">
                  <div className="card text-center">
                    <div className="card-body py-2">
                      <div className="text-muted small">Initial</div>
                      <div className="fw-bold">${initialValue?.toLocaleString('en-US', { minimumFractionDigits: 2 })}</div>
                    </div>
                  </div>
                </div>
                <div className="col-4">
                  <div className="card text-center">
                    <div className="card-body py-2">
                      <div className="text-muted small">Final</div>
                      <div className="fw-bold">${finalValue.toLocaleString('en-US', { minimumFractionDigits: 2 })}</div>
                    </div>
                  </div>
                </div>
                <div className="col-4">
                  <div className={`card text-center border-${gain >= 0 ? 'success' : 'danger'}`}>
                    <div className="card-body py-2">
                      <div className="text-muted small">Gain / Loss</div>
                      <div className={`fw-bold text-${gain >= 0 ? 'success' : 'danger'}`}>
                        {gain >= 0 ? '+' : ''}${gain?.toLocaleString('en-US', { minimumFractionDigits: 2 })}
                        {gainPct != null && <span className="ms-1 small">({gainPct >= 0 ? '+' : ''}{gainPct.toFixed(2)}%)</span>}
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            )}

            <h6>Trades ({result.Trades?.length ?? 0})</h6>
            <div style={{ maxHeight: 250, overflowY: 'auto' }}>
              <table className="table table-sm table-hover mb-0">
                <thead className="table-light sticky-top">
                  <tr>
                    <th>Date</th><th>Action</th><th>Ticker</th>
                    <th className="text-end">Price</th><th className="text-end">Shares</th>
                    <th className="text-end">Amount</th><th className="text-end">Cash After</th>
                  </tr>
                </thead>
                <tbody>
                  {(result.Trades || []).map((t, i) => (
                    <tr key={i}>
                      <td>{t.Date?.substring(0, 10)}</td>
                      <td><span className="badge bg-secondary">{t.Action}</span></td>
                      <td>{t.TickerCode}</td>
                      <td className="text-end">{t.Price > 0 ? `$${t.Price.toFixed(2)}` : '–'}</td>
                      <td className="text-end">{t.Shares > 0 ? Math.round(t.Shares) : '–'}</td>
                      <td className={`text-end ${t.Amount >= 0 ? 'text-success' : 'text-danger'}`}>
                        {t.Amount !== 0 ? `${t.Amount >= 0 ? '+' : ''}$${t.Amount.toFixed(2)}` : '–'}
                      </td>
                      <td className="text-end">${t.RemainingCash?.toFixed(2)}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>

            <h6 className="mt-3">Portfolio History ({history.length} days)</h6>
            <div style={{ maxHeight: 200, overflowY: 'auto' }}>
              <table className="table table-sm table-striped mb-0">
                <thead className="table-light sticky-top">
                  <tr>
                    <th>Date</th><th className="text-end">Total Value</th><th className="text-end">Cash</th>
                  </tr>
                </thead>
                <tbody>
                  {history.map((p, i) => (
                    <tr key={i}>
                      <td>{p.Date?.substring(0, 10)}</td>
                      <td className="text-end">${p.TotalValue?.toLocaleString('en-US', { minimumFractionDigits: 2 })}</td>
                      <td className="text-end">${p.Cash?.toLocaleString('en-US', { minimumFractionDigits: 2 })}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
          <div className="modal-footer">
            <button type="button" className="btn btn-secondary" onClick={onClose}>Close</button>
          </div>
        </div>
      </div>
    </div>
  );
}

// ── Settings form modal ────────────────────────────────────────────────────
function SettingsFormModal({ existing, allModels, onSave, onClose }) {
  const [name, setName] = useState(existing?.Name || '');
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');

  const handleSave = async () => {
    if (!name.trim()) { setError('Name is required'); return; }
    setSaving(true);
    setError('');
    try {
      const existing_settings = existing?.Settings;
      const settings = existing_settings || {
        ModelIds: [],
        InitialAmount: 10000,
        Stocks: [],
        CommissionPercent: 0.2,
        CombineMode: 'CbSoM',
      };
      const result = await ApiClient.savePerformanceCalculationSettings({
        Id: existing?.Id ?? null,
        Name: name.trim(),
        Settings: settings,
      });
      onSave(result.id, name.trim());
    } catch (exc) {
      setError(exc.message || 'Failed to save');
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="modal d-block" tabIndex="-1" style={{ background: 'rgba(0,0,0,0.5)' }}>
      <div className="modal-dialog">
        <div className="modal-content">
          <div className="modal-header">
            <h5 className="modal-title">{existing ? 'Rename Settings' : 'Create Settings'}</h5>
            <button type="button" className="btn-close" onClick={onClose} />
          </div>
          <div className="modal-body">
            {error && <div className="alert alert-danger py-2">{error}</div>}
            <label className="form-label fw-semibold">Name</label>
            <input
              type="text"
              className="form-control"
              value={name}
              onChange={e => setName(e.target.value)}
              autoFocus
              onKeyDown={e => e.key === 'Enter' && handleSave()}
            />
          </div>
          <div className="modal-footer">
            <button type="button" className="btn btn-secondary" onClick={onClose}>Cancel</button>
            <button type="button" className="btn btn-primary" onClick={handleSave} disabled={saving}>
              {saving ? <><span className="spinner-border spinner-border-sm me-2" />Saving…</> : 'Save'}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}

// ── Assignments modal ────────────────────────────────────────────────────
// Scoped to a single settings record (passed via `settingsItem`). Lists every
// (ModelId, Tag) → THIS settings assignment, plus an inline form to add a
// new (model, tag) pointing at this same settings record. The settings
// dropdown is locked to the current record — admins cannot assign a
// different settings record from this modal.
function AssignmentsModal({ settingsItem, onClose }) {
  const [loading, setLoading] = useState(true);
  const [loadError, setLoadError] = useState('');
  const [assignments, setAssignments] = useState([]);

  // Add-new state. The settings dropdown is locked to settingsItem.Id; we
  // only collect it in the form for clarity (and so the API call shape is
  // self-documenting).
  const [selectedModel, setSelectedModel] = useState(null);
  const [selectedTag, setSelectedTag] = useState(null);
  const [saving, setSaving] = useState(false);
  const [actionError, setActionError] = useState('');
  const [success, setSuccess] = useState('');

  // Per-row busy state (removing) so only one Remove button is busy at a time.
  const [removingKey, setRemovingKey] = useState(null);

  const settingsId = settingsItem?.Id;

  const loadAll = useCallback(async () => {
    setLoading(true);
    setLoadError('');
    try {
      // Fetch the full assignments list and filter client-side to this
      // settings record. Cheaper than a dedicated server filter for the
      // current data volumes, and keeps the controller endpoint generic.
      const data = await ApiClient.getPerformanceCalculationAssignments();
      setAssignments((data || []).filter(a => a.PerformanceCalculationId === settingsId));
    } catch (exc) {
      setLoadError(exc.message || 'Failed to load assignments');
    } finally {
      setLoading(false);
    }
  }, [settingsId]);

  useEffect(() => { loadAll(); }, [loadAll]);

  const loadModelOptions = async (inputValue, callback) => {
    if (!inputValue || inputValue.length < 1) { callback([]); return; }
    try {
      const data = await ApiClient.searchModels({ NamePartial: inputValue, Offset: 0, Limit: 30 });
      callback((data.Items || []).map(m => ({
        value: m.Id,
        label: `${m.Name || m.FileName} [#${m.Id}]`,
        signalTags: m.SignalTags || [],
        activeSignalTag: m.ActiveSignalTag || null,
      })));
    } catch { callback([]); }
  };

  const tagOptions = (selectedModel?.signalTags || []).map(t => ({ value: t, label: t }));
  const handleModelChange = (opt) => {
    setSelectedModel(opt);
    setSelectedTag(null);
  };

  // The settings record is fixed for this modal — admins can only add or
  // remove pairs that point at this same settings id. No picker, no PUT
  // payload for a different settings id, no scope creep.
  const handleAdd = async () => {
    setActionError('');
    setSuccess('');
    if (!selectedModel) { setActionError('Pick a model'); return; }
    if (!selectedTag) { setActionError('Pick a tag — defaults are per (model, tag)'); return; }

    setSaving(true);
    try {
      await ApiClient.setModelDefaultPerformanceCalculation(
        selectedModel.value,
        selectedTag.value,
        settingsId,
      );
      setSuccess(`Added ${selectedModel.label} / ${selectedTag.value} → ${settingsItem?.Name || `#${settingsId}`}`);
      setSelectedModel(null);
      setSelectedTag(null);
      await loadAll();
    } catch (exc) {
      setActionError(exc.message || 'Failed to add assignment');
    } finally {
      setSaving(false);
    }
  };

  const handleRemove = async (a) => {
    const label = `${a.ModelName || `#${a.ModelId}`} / ${a.Tag || '(no tag)'}`;
    if (!window.confirm(`Remove default for ${label}?`)) return;
    const key = `${a.ModelId}-${a.Tag}`;
    setRemovingKey(key);
    setActionError('');
    try {
      await ApiClient.removeModelDefaultPerformanceCalculation(a.ModelId, a.Tag);
      setSuccess(`Removed ${label}`);
      await loadAll();
    } catch (exc) {
      setActionError(exc.message || 'Failed to remove assignment');
    } finally {
      setRemovingKey(null);
    }
  };

  return (
    <div className="modal d-block" tabIndex="-1" style={{ background: 'rgba(0,0,0,0.5)' }}>
      <div className="modal-dialog modal-xl modal-dialog-scrollable">
        <div className="modal-content">
          <div className="modal-header">
            <h5 className="modal-title">
              Assignments for settings: <span className="fw-semibold">{settingsItem?.Name || `#${settingsId}`}</span>
            </h5>
            <button type="button" className="btn-close" onClick={onClose} />
          </div>
          <div className="modal-body">
            {loadError && <div className="alert alert-danger py-2">{loadError}</div>}
            {actionError && <div className="alert alert-danger py-2">{actionError}</div>}
            {success && <div className="alert alert-success py-2">{success}</div>}

            {/* ── Existing assignments table ──────────────────────────────── */}
            <h6 className="fw-semibold mb-2">
              (model, tag) pairs pointing at this settings record ({assignments.length})
            </h6>
            {loading ? (
              <div className="text-center py-3"><div className="spinner-border spinner-border-sm" /></div>
            ) : assignments.length === 0 ? (
              <p className="text-muted small mb-3">
                No (model, tag) pairs currently point at this settings record. Use the form below to add one.
              </p>
            ) : (
              <div className="border rounded mb-3" style={{ maxHeight: 280, overflowY: 'auto' }}>
                <table className="table table-sm mb-0 align-middle">
                  <thead className="table-light sticky-top">
                    <tr>
                      <th>Model</th>
                      <th>Tag</th>
                      <th style={{ width: 110 }} />
                    </tr>
                  </thead>
                  <tbody>
                    {assignments.map((a) => {
                      const key = `${a.ModelId}-${a.Tag}`;
                      return (
                        <tr key={key}>
                          <td>
                            {a.ModelName || `#${a.ModelId}`}
                            <span className="text-muted ms-1 small">#{a.ModelId}</span>
                          </td>
                          <td>
                            {a.Tag || <span className="text-muted fst-italic">(no tag)</span>}
                          </td>
                          <td className="text-end">
                            <button
                              type="button"
                              className="btn btn-outline-danger btn-sm"
                              onClick={() => handleRemove(a)}
                              disabled={removingKey === key}
                              title="Remove this (model, tag) → settings assignment"
                            >
                              {removingKey === key
                                ? <span className="spinner-border spinner-border-sm" />
                                : 'Remove'}
                            </button>
                          </td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              </div>
            )}

            {/* ── Add-new form ────────────────────────────────────────────── */}
            <h6 className="fw-semibold mb-2 mt-2">Add new (model, tag) pointing at this settings record</h6>
            <div className="border rounded p-3 bg-light">
              <div className="row g-2 align-items-end">
                <div className="col-md-5">
                  <label className="form-label fw-semibold small mb-1">Model</label>
                  <AsyncSelect
                    loadOptions={loadModelOptions}
                    value={selectedModel}
                    onChange={handleModelChange}
                    placeholder="Type to search model…"
                    isClearable
                    defaultOptions={false}
                  />
                </div>
                <div className="col-md-4">
                  <label className="form-label fw-semibold small mb-1">Tag</label>
                  <Select
                    options={tagOptions}
                    value={selectedTag}
                    onChange={setSelectedTag}
                    placeholder={selectedModel ? (tagOptions.length > 0 ? 'Pick a tag' : 'No tags on model') : 'Pick a model first'}
                    isDisabled={!selectedModel || tagOptions.length === 0}
                    isClearable
                  />
                </div>
                <div className="col-md-3">
                  <label className="form-label fw-semibold small mb-1">
                    Settings <span className="text-muted fw-normal">(locked)</span>
                  </label>
                  <div className="form-control-plaintext small">
                    <code>#{settingsId}</code> {settingsItem?.Name ? `· ${settingsItem.Name}` : ''}
                  </div>
                </div>
              </div>
              <div className="d-flex justify-content-end mt-2">
                <button
                  type="button"
                  className="btn btn-primary btn-sm"
                  onClick={handleAdd}
                  disabled={saving || !selectedModel || !selectedTag}
                >
                  {saving ? <><span className="spinner-border spinner-border-sm me-2" />Adding…</> : 'Add'}
                </button>
              </div>
              <div className="form-text mt-2">
                Adds a new (model, tag) → this settings assignment. Multiple tags on the same model can each point
                at this same settings record.
              </div>
            </div>
          </div>
          <div className="modal-footer">
            <button type="button" className="btn btn-secondary" onClick={onClose}>Close</button>
          </div>
        </div>
      </div>
    </div>
  );
}

// ── Main page ──────────────────────────────────────────────────────────────
export const PerformanceCalculationSettings = () => {
  const navigate = useNavigate();
  const { user } = useAuth();
  const isAdmin = user?.Roles?.includes('Admin');
  const [items, setItems] = useState([]);
  const [totalCount, setTotalCount] = useState(0);
  const [page, setPage] = useState(0);
  const [nameFilter, setNameFilter] = useState('');
  const [loading, setLoading] = useState(false);
  const [loadError, setLoadError] = useState('');

  const [showFormModal, setShowFormModal] = useState(false);
  const [editingItem, setEditingItem] = useState(null);

  const [showAssignmentsModal, setShowAssignmentsModal] = useState(false);

  const [resultModal, setResultModal] = useState(null);
  const [runningId, setRunningId] = useState(null);
  const [loadingResultId, setLoadingResultId] = useState(null);
  const [actionError, setActionError] = useState('');

  const totalPages = Math.ceil(totalCount / PAGE_SIZE);

  const load = useCallback(async (pageIndex, nameVal) => {
    setLoading(true);
    setLoadError('');
    try {
      const data = await ApiClient.searchPerformanceCalculationSettings({
        Offset: pageIndex * PAGE_SIZE,
        Limit: PAGE_SIZE,
        Name: nameVal || null,
      });
      setItems(data.Items || []);
      setTotalCount(data.TotalCount || 0);
    } catch (err) {
      setLoadError(err.message || 'Failed to load settings');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { load(page, nameFilter); }, [load, page, nameFilter]);

  const handleSearch = (e) => {
    e.preventDefault();
    setPage(0);
    load(0, nameFilter);
  };

  const handleDelete = async (item) => {
    if (!window.confirm(`Delete "${item.Name}"?`)) return;
    setActionError('');
    try {
      await ApiClient.deletePerformanceCalculationSettings(item.Id);
      load(page, nameFilter);
    } catch (err) {
      setActionError(err.message || 'Failed to delete');
    }
  };

  const handleRun = async (item) => {
    setRunningId(item.Id);
    setActionError('');
    try {
      const result = await ApiClient.runPerformanceCalculationSettings(item.Id);
      setResultModal(result);
    } catch (err) {
      setActionError(err.message || 'Calculation failed');
    } finally {
      setRunningId(null);
    }
  };

  const handleViewResult = async (item) => {
    setLoadingResultId(item.Id);
    setActionError('');
    try {
      const result = await ApiClient.getPerformanceCalculationSettingsResult(item.Id);
      setResultModal(result);
    } catch (err) {
      setActionError(err.message || 'No result available');
    } finally {
      setLoadingResultId(null);
    }
  };

  const handleLoadToCalculation = (item) => {
    navigate('/performance-calculation', { state: { loadSettings: item } });
  };

  const handleSaved = (id, name) => {
    setShowFormModal(false);
    setEditingItem(null);
    load(page, nameFilter);
  };

  const formatDate = (iso) => {
    if (!iso) return '–';
    try { return new Date(iso).toLocaleString(); } catch { return iso; }
  };

  const summarizeSettings = (s) => {
    if (!s) return '–';
    const parts = [];
    // Prefer the new Models shape; fall back to the legacy ModelIds list so
    // settings written before the per-tag migration still show a sensible
    // summary (with a marker so the admin knows it's missing explicit tags).
    if (s.Models?.length) {
      parts.push(`${s.Models.length} model+tag row(s)`);
    } else if (s.ModelIds?.length) {
      parts.push(`${s.ModelIds.length} model(s) (no explicit tag)`);
    }
    if (s.Stocks?.length) parts.push(`${s.Stocks.length} stock(s)`);
    if (s.InitialAmount) parts.push(`$${s.InitialAmount.toLocaleString()}`);
    return parts.join(', ') || '–';
  };

  // Renders the per-row "currently assigned to" badges.
  // Each badge is "{ModelName} / {Tag}". When the settings record is not
  // assigned anywhere we show a muted "—" so the column has a stable width
  return (
    <div>
      <div className="d-flex align-items-center mb-3 gap-3">
        <h2 className="mb-0">Performance Calculation Settings</h2>
        <button className="btn btn-success btn-sm" onClick={() => { setEditingItem(null); setShowFormModal(true); }}>
          + New
        </button>
        <button className="btn btn-outline-primary btn-sm" onClick={() => navigate('/performance-calculation')}>
          ← Go to Calculator
        </button>
      </div>

      <form className="row g-2 align-items-end mb-3" onSubmit={handleSearch}>
        <div className="col-md-4">
          <input
            type="text"
            className="form-control form-control-sm"
            placeholder="Filter by name…"
            value={nameFilter}
            onChange={e => setNameFilter(e.target.value)}
          />
        </div>
        <div className="col-auto">
          <button type="submit" className="btn btn-outline-secondary btn-sm">Search</button>
          {nameFilter && (
            <button type="button" className="btn btn-link btn-sm ms-1" onClick={() => { setNameFilter(''); setPage(0); load(0, ''); }}>
              Clear
            </button>
          )}
        </div>
      </form>

      {loadError && <div className="alert alert-danger">{loadError}</div>}
      {actionError && <div className="alert alert-warning alert-dismissible">
        {actionError}
        <button type="button" className="btn-close" onClick={() => setActionError('')} />
      </div>}

      {loading && <div className="text-center py-4"><div className="spinner-border" /></div>}

      {!loading && items.length === 0 && (
        <p className="text-muted">No saved settings yet. Click "+ New" to create one.</p>
      )}

      {!loading && items.length > 0 && (
        <>
          <table className="table table-hover table-sm align-middle">
            <thead className="table-light">
              <tr>
                <th>ID</th>
                <th>Name</th>
                <th>Settings</th>
                <th>Updated</th>
                <th style={{ minWidth: 280 }}>Actions</th>
              </tr>
            </thead>
            <tbody>
              {items.map(item => (
                <tr key={item.Id}>
                  <td className="text-muted small">{item.Id}</td>
                  <td className="fw-semibold">{item.Name}</td>
                  <td className="text-muted small">{summarizeSettings(item.Settings)}</td>
                  <td className="text-muted small">{formatDate(item.UpdateTime)}</td>
                  <td>
                    <div className="d-flex gap-1 flex-wrap">
                      <button
                        className="btn btn-outline-primary btn-sm"
                        onClick={() => handleLoadToCalculation(item)}
                        title="Load these settings into the calculator"
                      >
                        Load
                      </button>
                      <button
                        className="btn btn-success btn-sm"
                        onClick={() => handleRun(item)}
                        disabled={runningId === item.Id}
                        title="Run calculation and store result"
                      >
                        {runningId === item.Id
                          ? <><span className="spinner-border spinner-border-sm me-1" />Running…</>
                          : 'Run'}
                      </button>
                      <button
                        className="btn btn-outline-secondary btn-sm"
                        onClick={() => handleViewResult(item)}
                        disabled={loadingResultId === item.Id}
                        title="View stored result"
                      >
                        {loadingResultId === item.Id
                          ? <span className="spinner-border spinner-border-sm" />
                          : 'Result'}
                      </button>
                      <button
                        className="btn btn-outline-dark btn-sm"
                        onClick={() => { setEditingItem(item); setShowFormModal(true); }}
                        title="Rename"
                      >
                        Rename
                      </button>
                      <button
                        className="btn btn-outline-danger btn-sm"
                        onClick={() => handleDelete(item)}
                        title="Delete"
                      >
                        Delete
                      </button>
                      {isAdmin && (
                        <button
                          className="btn btn-outline-warning btn-sm"
                          onClick={() => setShowAssignmentsModal(item)}
                          title={`Show the (model, tag) pairs assigned as defaults for "${item.Name}", and add new ones`}
                        >
                          Show model assignments
                        </button>
                      )}
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>

          {totalPages > 1 && (
            <nav>
              <ul className="pagination pagination-sm">
                <li className={`page-item ${page === 0 ? 'disabled' : ''}`}>
                  <button className="page-link" onClick={() => setPage(p => p - 1)}>‹</button>
                </li>
                {Array.from({ length: totalPages }, (_, i) => (
                  <li key={i} className={`page-item ${i === page ? 'active' : ''}`}>
                    <button className="page-link" onClick={() => setPage(i)}>{i + 1}</button>
                  </li>
                ))}
                <li className={`page-item ${page >= totalPages - 1 ? 'disabled' : ''}`}>
                  <button className="page-link" onClick={() => setPage(p => p + 1)}>›</button>
                </li>
              </ul>
            </nav>
          )}
          <div className="text-muted small">{totalCount} item(s) total</div>
        </>
      )}

      {showFormModal && (
        <SettingsFormModal
          existing={editingItem}
          onSave={handleSaved}
          onClose={() => { setShowFormModal(false); setEditingItem(null); }}
        />
      )}

      {resultModal && (
        <ResultModal result={resultModal} onClose={() => setResultModal(null)} />
      )}

      {showAssignmentsModal && (
        <AssignmentsModal
          settingsItem={showAssignmentsModal}
          onClose={() => setShowAssignmentsModal(false)}
        />
      )}
    </div>
  );
};
